import { useDispatch, useSelector} from "react-redux";
import { useState } from "react";
import { fetchLogin, fetchRegister, clearMessages } from "../../../features/auth/authSlice";
import Modal from "../../../components/ui/Modal";

function Field({ label, type = "text", name, value, onChange, error, placeholder }) {
    const [show, setShow] = useState(false);
    const isPassword = type === "password";

    return (
        <div className="flex flex-col md:gap-1">
            <label className="text-sm font-medium text-gray-700">{label}</label>
            <div className="relative">
                <input
                    type={isPassword && show ? "text" : type}
                    name={name}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    className={`w-full px-4 py-2.5 rounded-xl border text-sm outline-none transition-all
                        ${error
                            ? "border-red-400 bg-red-50 focus:ring-2 focus:ring-red-200"
                            : "border-gray-200 bg-gray-50 focus:border-[var(--primary-medium)] focus:ring-2 focus:ring-[var(--primary-extrasoft)]"
                        }`}
                />
                {isPassword && (
                    <button
                        type="button"
                        onClick={() => setShow(p => !p)}
                        className="cursor-pointer absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                    >
                        {show ? "Hide" : "Show"}
                    </button>
                )}
            </div>
            {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
    );
}
    function validateLogin({ phone, password }) {
        
        const errors = {};
        if (!phone) errors.phone = "Phone or email is required";
        if (!password) errors.password = "Password is required";
        else if (password.length < 6) errors.password = "Minimum 6 characters";
        return errors;
    }

    function validateRegister({ email, phone, password, confirm }) {
        const errors = {};
        if (!email) errors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(email)) errors.email = "Enter a valid email";
        if (!phone) errors.phone = "Phone is required";
        else if (!/^\d{10}$/.test(phone)) errors.phone = "Enter a valid 10-digit number";
        if (!password) errors.password = "Password is required";
        else if (password.length < 6) errors.password = "Minimum 6 characters";
        if (!confirm) errors.confirm = "Please confirm password";
        else if (confirm !== password) errors.confirm = "Passwords do not match";
        return errors;
    }
// ─── LoginForm ────────────────────────────────────────────────────────
function LoginForm({ onSwitch, onClose }) {
    const dispatch = useDispatch();
    const { loading, error, success } = useSelector((state) => state.auth);
    const [form, setForm] = useState({ phone: "", password: "" });
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        setForm(p => ({ ...p, [e.target.name]: e.target.value }));
        setErrors(p => ({ ...p, [e.target.name]: "" }));
        dispatch(clearMessages());
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validateLogin(form);
        if (Object.keys(errs).length) { setErrors(errs); return; }

        const result = await dispatch(fetchLogin({
            phone: form.phone,
            password: form.password
        }));

        // ✅ close modal on success
        if (fetchLogin.fulfilled.match(result)) onClose();
    };

    return (
        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
            <Field
                label="Phone / Email"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                error={errors.phone}
                placeholder="Phone number or email"
            />
            <Field
                label="Password"
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                error={errors.password}
                placeholder="••••••••"
            />

            {/* ✅ API error from Redux */}
            {error && (
                <p className="text-xs text-red-500 text-center">{error}</p>
            )}

            <button
                type="submit"
                disabled={loading}
                className="cursor-pointer w-full py-3 rounded-xl bg-[var(--primary-medium)] text-white font-semibold text-sm
                    hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
            >
                {loading ? "Signing in..." : "Sign In"}
            </button>

            <p className="text-center text-sm text-gray-500">
                Don't have an account?{" "}
                <button type="button" onClick={onSwitch} className="cursor-pointer text-[var(--primary-medium)] font-medium hover:underline">
                    Register
                </button>
            </p>
        </form>
    );
}

// ─── RegisterForm ─────────────────────────────────────────────────────
function RegisterForm({ onSwitch }) {
    const dispatch = useDispatch();
    const { loading, error, success } = useSelector((state) => state.auth);
    const [form, setForm] = useState({ email: "", phone: "", password: "", confirm: "" });
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        setForm(p => ({ ...p, [e.target.name]: e.target.value }));
        setErrors(p => ({ ...p, [e.target.name]: "" }));
        dispatch(clearMessages());
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validateRegister(form);
        if (Object.keys(errs).length) { setErrors(errs); return; }

        const result = await dispatch(fetchRegister({
            fName: form.fName,
            lName: form.lName,
            email: form.email,
            phone: form.phone,
            password: form.password,
        }));

        // ✅ switch to login on successful register
        if (fetchRegister.fulfilled.match(result)) onSwitch();
    };

    return (
        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
               <Field
                label="First Name"
                type="text"
                name="fName"
                value={form.fName}
                onChange={handleChange}
                error={errors.fName}
                placeholder="abc"
            />
            <Field
                label="Last Name"
                type="text"
                name="lName"
                value={form.lName}
                onChange={handleChange}
                error={errors.lName}
                placeholder="abc"
            />
            <Field
                label="Email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                error={errors.email}
                placeholder="you@example.com"
            />
            <Field
                label="Phone Number"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                error={errors.phone}
                placeholder=""
            />
            <Field
                label="Password"
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                error={errors.password}
                placeholder="••••••••"
            />
            <Field
                label="Confirm Password"
                type="password"
                name="confirm"
                value={form.confirm}
                onChange={handleChange}
                error={errors.confirm}
                placeholder="••••••••"
            />

            {/* ✅ API error or success from Redux */}
            {error && <p className="text-xs text-red-500 text-center">{error}</p>}
            {success && <p className="text-xs text-green-500 text-center">{success}</p>}

            <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl bg-[var(--primary-medium)] text-white font-semibold text-sm
                    hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
            >
                {loading ? "Creating account..." : "Create Account"}
            </button>

            <p className="text-center text-sm text-gray-500">
                Already have an account?{" "}
                <button type="button" onClick={onSwitch} className="cursor-pointer text-[var(--primary-medium)] font-medium hover:underline">
                    Sign In
                </button>
            </p>
        </form>
    );
}

// ─── AuthModal ────────────────────────────────────────────────────────
export default function AuthModal({ isOpen, onClose }) {
    const [tab, setTab] = useState("login");
    const dispatch = useDispatch();

    const handleClose = () => {
        setTab("login");
        dispatch(clearMessages());
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} maxWidth="max-w-xl">
            <div className="md:p-8 p-4">
                <div className="flex rounded-xl bg-gray-100 p-1 mb-6">
                    {["login", "register"].map(t => (
                        <button
                            key={t}
                            onClick={() => { setTab(t); dispatch(clearMessages()); }}
                            className={`cursor-pointer flex-1 py-2 rounded-lg text-sm font-semibold transition-all capitalize
                                ${tab === t
                                    ? "bg-white text-black shadow-sm"
                                    : "text-gray-500 hover:text-black"
                                }`}
                        >
                            {t === "login" ? "Sign In" : "Register"}
                        </button>
                    ))}
                </div>

                <h2 className="text-xl font-bold text-black mb-1">
                    {tab === "login" ? "Welcome back" : "Create account"}
                </h2>
                <p className="text-sm text-gray-500 mb-6 ">
                    {tab === "login"
                        ? "Sign in to your Apsensys Care account"
                        : "Join Apsensys Care today"}
                </p>

                {tab === "login"
                    ? <LoginForm onSwitch={() => setTab("register")} onClose={handleClose} />
                    : <RegisterForm onSwitch={() => setTab("login")} />
                }
            </div>
        </Modal>
    );
}